import {
  batchWrite,
  deleteDocument,
  getDocument,
  queryDocuments,
  updateDocument,
} from '../services/firestoreUtilities';
import { BatchOperation } from '../services/types';
import { addStatus } from '../status/statusActions';
import {
  Organization,
  OrganizationDoc,
  OrganizationUser,
  UserInvite,
} from './types';

export const getOrganization = async (orgId: string) => {
  if (!orgId) {
    return;
  }
  try {
    const { getFirestoreInstance } = await import('../services/firebaseConfig');
    const firestore = await getFirestoreInstance();
    const doc = await getDocument<OrganizationDoc>(
      firestore,
      'organizations',
      orgId,
    );

    if (!doc) {
      addStatus({
        type: 'error',
        message: 'Organization not found',
        autoDismiss: true,
      });
      return null;
    }
    const org = {
      ...doc,
      createdAt: doc.createdAt.toDate(),
      updatedAt: doc.updatedAt.toDate(),
    };

    return org as Organization;
  } catch (error) {
    console.error('Error getting organization:', error);
  }
};

export const updateOrganization = async (
  organization: Partial<Organization> & { id: string },
) => {
  const { getFirestoreInstance } = await import('../services/firebaseConfig');
  try {
    const firestore = await getFirestoreInstance();
    updateDocument(firestore, 'organizations', organization.id, organization);
  } catch (error) {
    console.error('Error updating organization:', error);
  }
};

export const getOrganizationMembers = async (
  orgId: string,
): Promise<OrganizationUser[] | null> => {
  const { getFirestoreInstance } = await import('../services/firebaseConfig');
  const where = (await import('firebase/firestore')).where;
  try {
    const firestore = await getFirestoreInstance();
    const data = await queryDocuments<OrganizationUser>(
      firestore,
      'memberships',
      where('organizationId', '==', orgId),
    );
    if (!data.documents) {
      addStatus({
        type: 'error',
        message: 'Members not found',
        autoDismiss: true,
      });
      return null;
    }
    return data.documents;
  } catch (error) {
    console.error('Error getting organization members:', error);
    return null;
  }
};

export const updateOrganizationMembers = async (
  operations: BatchOperation[],
) => {
  const { getFirestoreInstance } = await import('../services/firebaseConfig');
  const firestore = await getFirestoreInstance();
  try {
    await batchWrite(firestore, operations);
    addStatus({
      type: 'success',
      message: 'Organization members updated successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error updating organization members:', error);
    addStatus({
      type: 'error',
      message: 'Failed to update organization members',
      autoDismiss: true,
    });
  }
};

export const getOrgUserInvites = async (
  orgId: string,
): Promise<UserInvite[] | null> => {
  const { getFirestoreInstance } = await import('../services/firebaseConfig');
  const where = (await import('firebase/firestore')).where;
  try {
    const firestore = await getFirestoreInstance();
    const data = await queryDocuments<UserInvite>(
      firestore,
      'invites',
      where('organizationId', '==', orgId),
      where('status', '==', 'pending'),
    );
    if (!data.documents) {
      addStatus({
        type: 'error',
        message: 'No user invites found',
        autoDismiss: true,
      });
      return null;
    }
    return data.documents;
  } catch (error) {
    console.error('Error getting organization user invites:', error);
    return null;
  }
};

export const deleteOrgUserInvite = async (inviteId: string) => {
  const { getFirestoreInstance } = await import('../services/firebaseConfig');
  const firestore = await getFirestoreInstance();
  try {
    await deleteDocument(firestore, 'invites', inviteId);
  } catch (error) {
    console.error('Error deleting organization user invite:', error);
    throw error;
  }
};

export const listenToOrganizationChanges = async (
  orgId: string,
  callback: (org: Organization | null) => void,
) => {
  const { getFirestoreInstance } = await import('../services/firebaseConfig');
  const { listenToFirestoreDocument } =
    await import('../services/firestoreUtilities');

  const firestore = await getFirestoreInstance();
  return listenToFirestoreDocument<OrganizationDoc>(
    firestore,
    'organizations',
    orgId,
    (doc) => {
      if (doc) {
        const org = {
          ...doc,
          createdAt: doc.createdAt.toDate(),
          updatedAt: doc.updatedAt.toDate(),
        };
        callback(org as Organization);
      } else {
        callback(null);
      }
    },
  );
};
